import React from "react";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import {
  IAppState,
  ErrorDisplay,
  LoadingDisplay,
  LoadingState,
  ResponseError
} from "../../../../Common";
import { Workflow } from "../../../../Workflows/Models/Workflow";
import { WorkflowSelector } from "../../../../Workflows";
import {
  IGetTopicWorkflowsArguments,
  IUnloadTopicWorkflowsArguments,
  ISetTopicWorkflowsLoadingArguments,
  TopicWorkflowAction,
  getWorkflows,
  unloadWorkflows,
  setLoading
} from "../../../Actions/TopicWorkflowActions";

type Props = {
  selectedWorkflowCode?: string;
  onSelectedWorkflowChange: (newCode: string) => void;
};

type StateProps = {
  workflows: Workflow[];
  workflowLoadingState: LoadingState;
  workflowErrorMessage: string;
  workflowErrorCode?: number;
};

type DispatchProps = {
  getWorkflows: (args: IGetTopicWorkflowsArguments) => void;
  unloadWorkflows: (args: IUnloadTopicWorkflowsArguments) => void;
  setLoading: (args: ISetTopicWorkflowsLoadingArguments) => void;
};

const mapStateToProps = (state: IAppState): StateProps => ({
  workflows: state.topicWorkflow.workflows,
  workflowLoadingState: state.topicWorkflow.loadingState,
  workflowErrorMessage: state.topicWorkflow.errorMessage,
  workflowErrorCode: state.topicWorkflow.errorCode
});

const mapDispatchToProps = (dispatch: ThunkDispatch<IAppState, {}, TopicWorkflowAction>) => ({
  getWorkflows: (args: IGetTopicWorkflowsArguments) => dispatch(getWorkflows(args)),
  unloadWorkflows: (args: IUnloadTopicWorkflowsArguments) => dispatch(unloadWorkflows(args)),
  setLoading: (args: ISetTopicWorkflowsLoadingArguments) => dispatch(setLoading(args))
});

class TopicWorkflowSelectorContainer extends React.Component<Props & StateProps & DispatchProps> {
  constructor(props: Props & StateProps & DispatchProps) {
    super(props);

    this.onSelectedWorkflowChange = this.onSelectedWorkflowChange.bind(this);
  }

  componentDidMount = () => this.loadIfNeeded(this.props);

  componentDidUpdate = (prevProps: Props & StateProps) => this.loadIfNeeded(prevProps);

  componentWillUnmount = () => this.props.unloadWorkflows({});

  onSelectedWorkflowChange(workflow: Workflow) {
    this.props.onSelectedWorkflowChange(workflow.code);
  }

  loadIfNeeded(prevProps: Props & StateProps) {
    const { isLoaded, isLoading, errored } = this.props.workflowLoadingState;

    if (!isLoaded && !isLoading && !errored) {
      this.reload();
    }
  }

  // dispatch the actions to load the workflows
  reload() {
    this.props.setLoading({});
    this.props.getWorkflows({});
  }

  render() {
    const { isLoaded, isLoading, errored } = this.props.workflowLoadingState;

    let selectedWorkflow: Workflow | undefined;

    if (this.props.selectedWorkflowCode) {
      const selectedWorkflows = this.props.workflows.filter(
        w => w.code === this.props.selectedWorkflowCode
      );
      selectedWorkflow = selectedWorkflows.length === 1 ? selectedWorkflows[0] : undefined;
    }

    if (errored) {
      return (
        <ErrorDisplay
          {...this.props}
          errorDescription={this.props.workflowErrorMessage}
          errorResult="Could not retrieve Workflows from server"
        />
      );
    }
    if (isLoading) {
      return <LoadingDisplay />;
    }
    if (isLoaded) {
      return (
        <WorkflowSelector
          workflowOptions={this.props.workflows}
          selectedWorkflow={selectedWorkflow}
          onSelectedWorkflowChange={this.onSelectedWorkflowChange}
        />
      );
    }

    return <></>;
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TopicWorkflowSelectorContainer);
